import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronDown, ChevronUp, ListChecks } from "lucide-react";

export default function QuizzesAdmin() {
    const [quizzes, setQuizzes] = useState([]);
    const [courses, setCourses] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ course_id: "", title: "", time_limit_seconds: 600, pass_percentage: 60, randomize: true, is_final: false, max_attempts: 3 });
    const [editing, setEditing] = useState(null);
    const [qForm, setQForm] = useState({ type: "SINGLE", text: "", options: [{ text: "", is_correct: false }, { text: "", is_correct: false }] });
    const [qOpen, setQOpen] = useState(false);
    const [expanded, setExpanded] = useState({});

    const load = async () => {
        const [q, c] = await Promise.all([api.get("/quizzes"), api.get("/courses")]);
        setQuizzes(q.data.items);
        setCourses(c.data.items);
        if (editing) {
            const { data } = await api.get(`/quizzes/${editing.id}`);
            setEditing(data);
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    const createQuiz = async (e) => {
        e.preventDefault();
        await api.post("/quizzes", form);
        toast.success("Quiz created");
        setOpen(false);
        setForm({ course_id: "", title: "", time_limit_seconds: 600, pass_percentage: 60, randomize: true, is_final: false, max_attempts: 3 });
        load();
    };

    const openEdit = async (quiz) => {
        const { data } = await api.get(`/quizzes/${quiz.id}`);
        setEditing(data);
    };

    const addQuestion = async () => {
        if (!qForm.text || qForm.options.filter(o => o.text).length < 2) { toast.error("Add at least 2 options"); return; }
        if (!qForm.options.some(o => o.is_correct)) { toast.error("Mark at least one option as correct"); return; }
        await api.post(`/quizzes/${editing.id}/questions`, qForm);
        toast.success("Question added");
        setQForm({ type: "SINGLE", text: "", options: [{ text: "", is_correct: false }, { text: "", is_correct: false }] });
        setQOpen(false);
        load();
    };

    const deleteQ = async (qid) => {
        if (!window.confirm("Delete this question?")) return;
        await api.delete(`/questions/${qid}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div className="space-y-6" data-testid="admin-quizzes-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Manage</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-1">Quizzes</h1>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="mx-btn-primary" data-testid="admin-add-quiz-btn"><Plus className="h-4 w-4 mr-2" /> New quiz</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>Create quiz</DialogTitle></DialogHeader>
                        <form onSubmit={createQuiz} className="space-y-3">
                            <div><Label>Course</Label>
                                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                                    <SelectTrigger data-testid="quiz-course"><SelectValue placeholder="Select course" /></SelectTrigger>
                                    <SelectContent>{courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Title</Label><Input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="quiz-title" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label>Time limit (sec)</Label><Input type="number" value={form.time_limit_seconds} onChange={e => setForm({ ...form, time_limit_seconds: +e.target.value })} /></div>
                                <div><Label>Pass %</Label><Input type="number" value={form.pass_percentage} onChange={e => setForm({ ...form, pass_percentage: +e.target.value })} /></div>
                            </div>
                            <div className="flex items-center gap-3"><Switch checked={form.is_final} onCheckedChange={(v) => setForm({ ...form, is_final: v })} /><Label className="!mt-0">Final quiz (gates certificate)</Label></div>
                            <DialogFooter><Button type="submit" disabled={!form.course_id} className="mx-btn-primary" data-testid="quiz-submit">Create</Button></DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid gap-4">
                {quizzes.map(q => (
                    <Card key={q.id} className="mx-card" data-testid={`admin-quiz-${q.id}`}>
                        <CardContent className="p-5 flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-amber-gradient flex items-center justify-center text-white"><ListChecks className="h-5 w-5" /></div>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{q.course_title}</div>
                                <div className="font-bold mt-1">{q.title}</div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <Badge variant="outline" className="text-[10px]">{Math.round(q.time_limit_seconds / 60)} min</Badge>
                                    <Badge variant="outline" className="text-[10px]">Pass {q.pass_percentage}%</Badge>
                                    {q.is_final && <Badge className="bg-[#FFA81B] hover:bg-[#FFA81B] text-[10px]">Final</Badge>}
                                </div>
                            </div>
                            <Button variant="outline" className="rounded-xl" onClick={() => openEdit(q)} data-testid={`edit-quiz-${q.id}`}>Manage questions</Button>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
                    <DialogHeader><DialogTitle>{editing?.title} — questions</DialogTitle></DialogHeader>
                    {editing && (
                        <div className="space-y-3">
                            {editing.questions?.map((q, i) => (
                                <div key={q.id} className="border border-border rounded-xl p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="text-xs text-muted-foreground">Q{i + 1} · {q.type}</div>
                                            <div className="font-semibold mt-1">{q.text}</div>
                                            <ul className="mt-2 space-y-1">
                                                {q.options.map(o => (
                                                    <li key={o.id} className={`text-sm pl-3 ${o.is_correct ? "text-[#16A34A] font-semibold" : ""}`}>{o.is_correct && "✓ "}{o.text}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => deleteQ(q.id)}><Trash2 className="h-4 w-4 text-[#E11D48]" /></Button>
                                    </div>
                                </div>
                            ))}
                            <Dialog open={qOpen} onOpenChange={setQOpen}>
                                <DialogTrigger asChild><Button variant="outline" className="w-full rounded-xl" data-testid="add-question-btn"><Plus className="h-4 w-4 mr-2" /> Add question</Button></DialogTrigger>
                                <DialogContent className="max-w-lg">
                                    <DialogHeader><DialogTitle>New question</DialogTitle></DialogHeader>
                                    <div className="space-y-3">
                                        <div><Label>Type</Label>
                                            <Select value={qForm.type} onValueChange={(v) => setQForm({ ...qForm, type: v })}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="SINGLE">Single choice</SelectItem>
                                                    <SelectItem value="MULTI">Multiple choice</SelectItem>
                                                    <SelectItem value="TRUEFALSE">True / False</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>Question</Label><Textarea value={qForm.text} onChange={e => setQForm({ ...qForm, text: e.target.value })} data-testid="question-text" /></div>
                                        <div>
                                            <Label>Options</Label>
                                            <div className="space-y-2">
                                                {qForm.options.map((o, idx) => (
                                                    <div key={idx} className="flex gap-2 items-center">
                                                        <input type="checkbox" checked={o.is_correct} onChange={(e) => {
                                                            const opts = [...qForm.options];
                                                            opts[idx].is_correct = e.target.checked;
                                                            setQForm({ ...qForm, options: opts });
                                                        }} className="accent-[#16A34A]" />
                                                        <Input value={o.text} onChange={(e) => {
                                                            const opts = [...qForm.options];
                                                            opts[idx].text = e.target.value;
                                                            setQForm({ ...qForm, options: opts });
                                                        }} placeholder={`Option ${idx + 1}`} />
                                                    </div>
                                                ))}
                                                <Button variant="ghost" size="sm" onClick={() => setQForm({ ...qForm, options: [...qForm.options, { text: "", is_correct: false }] })}>+ Add option</Button>
                                            </div>
                                        </div>
                                        <DialogFooter><Button onClick={addQuestion} className="mx-btn-primary" data-testid="question-submit">Add question</Button></DialogFooter>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
